import * as React from "react"
import { useFlags } from "launchdarkly-react-client-sdk"
import { MdInfoOutline } from "react-icons/md"
import {
  useCmsIntegrationsForSiteQuery,
  useActiveCmsIntegrationsForSiteQuery,
} from "./queries.generated"
import {
  dataSourceLogos,
  dataSourceNames,
  dataLayerReadyIntegrations,
} from "../../constants/data-sources"
import { Badge, Button } from "gatsby-interface"
import { CmsVendor, CmsProvider } from "@gatsbyjs/graphql"
import { Loading } from "gatsby-ui/src/components"
import { FiArrowRight, FiEdit } from "react-icons/fi"
import { CheckIcon } from "gatsby-interface"
import { ErrorAlert } from "../shared/error-alert"
import { MdLaunch } from "react-icons/md"
import * as styles from "./quick-integrations.css"

export type QuickIntegrationsProps = {
  siteId: string
  onEdit: React.Dispatch<React.SetStateAction<CmsProvider | null>>
  onRemove: React.Dispatch<React.SetStateAction<CmsProvider | null>>
  controls: JSX.Element
  permissionToEdit: boolean
}

export function QuickIntegrations({
  siteId,
  onEdit,
  onRemove,
  controls,
  permissionToEdit,
}: QuickIntegrationsProps) {
  // gets available Quick Connect Cms integrations
  const cmsIntegrationsForSite = useCmsIntegrationsForSiteQuery({
    variables: {
      siteId,
    },
  })

  // checks if there is at least one integration connected
  const isConnected =
    cmsIntegrationsForSite?.data?.cmsIntegrationsForSite?.some(
      item => item.connected
    )

  // if isConnected gets the details of connections
  const activeIntegrationsForSite = useActiveCmsIntegrationsForSiteQuery({
    variables: {
      siteId,
    },
    skip: !isConnected,
  })

  const flags = useFlags()

  const cmsIntegrations =
    cmsIntegrationsForSite.data?.cmsIntegrationsForSite || []

  const activeIntegrations =
    activeIntegrationsForSite.data?.activeCmsIntegrationsForSite || []

  // injects (if applicable) connection config into integrations data
  const integrations = cmsIntegrations.map(item => {
    const integration: CmsProvider & {
      config?: JSON
      readyForDataLayer?: boolean
    } = {
      ...item,
    }

    if (dataLayerReadyIntegrations.includes(item.name)) {
      integration.readyForDataLayer = true
    }

    if (integration.connected) {
      const relatedActiveIntegration = activeIntegrations.find(
        activeIntegration => activeIntegration.vendor === integration.name
      )

      return {
        ...integration,
        ...relatedActiveIntegration,
      }
    }

    return integration
  })

  if (cmsIntegrationsForSite.loading || activeIntegrationsForSite.loading) {
    return <Loading />
  }

  if (cmsIntegrationsForSite.error || activeIntegrationsForSite.error) {
    return (
      <ErrorAlert>
        {cmsIntegrationsForSite.error?.message ||
          activeIntegrationsForSite.error?.message}
      </ErrorAlert>
    )
  }

  return (
    <>
      <div>
        {integrations.map(item => {
          const Logo = dataSourceLogos?.[item.name]
          const name = dataSourceNames?.[item.name]

          let sourceName
          let sourceUrl

          if (item.config) {
            // config objects are not normalized, different CMS provides config object with custom keys,
            // e.g Contentful config provides 'spaceName' but DatoCMS provides 'projectName'
            // to find the source name it looks for a property key ending with '...Name'
            const configItems = Object.entries(item.config)
            sourceName = configItems.find(item => item[0].endsWith(`Name`))?.[1]
            sourceUrl = item.config.managementUrl
          }

          return (
            <div key={item.name} className={styles.integration}>
              <span className={styles.integrationInfo}>
                {Logo && <Logo className={styles.integrationLogo} />}{" "}
                {name ? name : item.name}
              </span>
              {item.readyForDataLayer && (
                <Badge
                  size="S"
                  tone="SUCCESS"
                  textVariant="DEFAULT"
                  className={styles.integrationReady}
                >
                  Content Hub ready
                </Badge>
              )}
              {permissionToEdit && (
                <div className={styles.integrationActions}>
                  <Button
                    size="M"
                    variant="GHOST"
                    rightIcon={item.connected ? <FiEdit /> : <FiArrowRight />}
                    onClick={() => onEdit(item as CmsProvider)}
                  >
                    {item.connected ? `Edit` : `Connect`}
                  </Button>
                  {item.connected && (
                    <Button
                      size="M"
                      variant="GHOST"
                      tone="DANGER"
                      onClick={() => onRemove(item as CmsProvider)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              )}
              {item.connected && (
                <div className={styles.connectionDetails}>
                  <Badge size="S" tone="SUCCESS" textVariant="DEFAULT">
                    Connected
                  </Badge>
                  {sourceName && sourceUrl && (
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className={styles.connectionSourceLink}
                    >
                      {sourceName}&nbsp;
                      <MdLaunch />
                    </a>
                  )}
                  <div className={styles.connectionFeatures}>
                    {item.features?.envVars && (
                      <span className={styles.connectionFeature}>
                        <CheckIcon
                          className={styles.connectionFeatureStatusIcon}
                        />
                        Environment variables
                      </span>
                    )}
                    {item.features?.webhooks && (
                      <span className={styles.connectionFeature}>
                        <CheckIcon
                          className={styles.connectionFeatureStatusIcon}
                        />
                        Build webhook
                      </span>
                    )}
                    {item.features?.extension && (
                      <span className={styles.connectionFeature}>
                        <CheckIcon
                          className={styles.connectionFeatureStatusIcon}
                        />
                        Preview extension
                      </span>
                    )}
                  </div>
                  {flags.contentfulAppLink &&
                    item.vendor === CmsVendor.Contentful && (
                      <div className={styles.contentfulCallout}>
                        <MdInfoOutline size={20} className={styles.infoIcon} />
                        <div>
                          <a
                            href={`https://app.contentful.com/spaces/${item.config.spaceId}/apps/gatsby`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Configure the Gatsby Contentful App
                          </a>{" "}
                          to enable the Gatsby Preview widget in Contentful.
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {controls}
    </>
  )
}
